
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

/**
 * Hook for handling single provider deletion
 */
export const useProviderDelete = (
  setIsDeletingProvider: React.Dispatch<React.SetStateAction<boolean>>, 
  refetch: () => Promise<unknown>
) => {
  const { toast } = useToast();

  const handleProviderDelete = async (providerId: string): Promise<boolean> => {
    try {
      console.log(`Deleting provider with ID: ${providerId}`);
      setIsDeletingProvider(true);
      
      // Call RPC function to delete provider
      const { data, error } = await supabase
        .rpc('delete_peo_provider', { provider_id: providerId });

      if (error) {
        console.error('Error deleting provider:', error);
        toast({
          title: 'Error',
          description: `Failed to delete provider: ${error.message}`,
          variant: 'destructive',
        });
        return false;
      }

      if (!data) {
        console.error('Provider deletion failed but no error was returned');
        toast({
          title: 'Error',
          description: 'Provider deletion failed unexpectedly',
          variant: 'destructive',
        });
        return false;
      }
      
      console.log(`Provider successfully deleted`);
      toast({
        title: 'Success',
        description: `Provider deleted successfully`,
      });
      
      // Refresh data
      await refetch();
      return true;
    } catch (error: any) {
      console.error('Provider deletion error:', error);
      toast({
        title: 'Error',
        description: `Failed to delete provider: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsDeletingProvider(false);
    }
  };

  return { handleProviderDelete };
};
